import axios from 'axios';
import { DEV_SERVER_URL, PROD_SERVER_URL, LOCAL_SERVER_URL } from '../../constants/utils.constants';

const getBaseUrl = (): string => {
  const { hostname } = window.location;

  let baseUrl: string;
  
  // Check if hostname is localhost or local IP address (192.168.x.x, 10.x.x.x, 172.16-31.x.x, 127.x.x.x)
  const isLocalHost = hostname === 'localhost' || 
    hostname === '127.0.0.1' ||
    /^192\.168\.\d+\.\d+$/.test(hostname) ||
    /^10\.\d+\.\d+\.\d+$/.test(hostname) ||
    /^172\.(1[6-9]|2[0-9]|3[0-1])\.\d+\.\d+$/.test(hostname);
  
  switch (hostname) {
    case 'localhost':
    case '127.0.0.1':
      baseUrl = LOCAL_SERVER_URL;
      break;
    case 'shanenli-web-app.fly.dev':
      baseUrl = DEV_SERVER_URL;
      break;
    case 'app.shanen.li':
      baseUrl = PROD_SERVER_URL;
      break;
    default:
      // If it's a local IP address, use local server
      if (isLocalHost) {
        // Replace the IP with localhost for the server URL, or use the same IP
        baseUrl = `http://${hostname}:3000/`;
      } else {
        baseUrl = DEV_SERVER_URL;
      }
      break;
  }
  
  return baseUrl;
};

const baseURL = getBaseUrl();

const axiosInstance = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // Increased to 30 seconds
});

// Add a request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');

    // Check if the request is to a public endpoint or login endpoint
    const isPublicRequest = config.url?.includes('/public') || config.url?.includes('/login');
    
    if (token && !isPublicRequest) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (!token && !isPublicRequest) {
      window.location.href = '/';
      return Promise.reject('No token available');
    }
    
    return config;
  },
  (error) => {
    console.error('Axios request interceptor error:', error);
    return Promise.reject(error);
  }
);

/**
 * פונקציה לניקוי רק נתוני אימות מה-storage
 * שומרת נתונים חשובים כמו העדפות שפה, נתוני cache וכו'
 */
const clearAuthData = () => {
  // רשימת המפתחות שצריך לשמור
  const keysToKeep = [
    'i18nextLng', // העדפת שפה
    'theme', // נושא
    'preferences', // העדפות כלליות
    'analytics', // נתוני אנליטיקס
    'feature_flags', // דגלי תכונות
    // הוסף כאן מפתחות נוספים שרצית לשמור
  ];
  
  // שמירת הערכים שצריך לשמור מ-localStorage
  const savedValues: Record<string, string> = {};
  keysToKeep.forEach(key => {
    const value = localStorage.getItem(key);
    if (value) {
      savedValues[key] = value;
    }
  });
  
  // ניקוי localStorage
  localStorage.clear();
  
  // החזרת הערכים ששמרנו
  Object.entries(savedValues).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });
  
  // ניקוי sessionStorage (כאן גם אפשר להוסיף לוגיקת שמירה אם צריך)
  sessionStorage.clear();
  
};

// Add a response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    console.error('Request failed:', error.response?.status, error.response?.statusText, error.config?.url);
    console.error('Error details:', error.message);
    console.error('Error status:', error?.response?.status);
    
    // אם זו שגיאת רשת (השרת לא זמין) והבקשה היא לנתוני משתמש
    if (!error.response && error.config?.url?.includes('/get-user-data')) {
      console.error('Network error while fetching user data - server might be down');
      clearAuthData();
      window.location.href = '/';
    }
    
    // אם התקבלה שגיאת 401 (לא מורשה), נקה את נתוני האימות ונווט לדף האימות
    if (error.response?.status === 401) {
      console.error('Unauthorized access - redirecting to auth page');
      clearAuthData();
      window.location.href = '/';
    }
    
    return Promise.reject(error);
  }
);

export { clearAuthData };
export default axiosInstance;
